import type {AnyKindOfStop} from './google-maps';
import { formatAddress} from './google-maps'
import {Warning} from './helpers/errors'
import {loadGoogleMapsApi} from './util/load-script'
import {arraySum} from './util/collections'
import {round} from './util/math'

/**
 * Computes the total driving distance between addresses. Result in meters.
 *
 * @param {object[]} stops
 * @param {string} apiKey
 * @returns {Promise}
 */
export default function calculateDistance(stops: (string|AnyKindOfStop)[], apiKey: string = window.env.GOOGLE_MAPS_API_KEY): Promise<number> {
    return new Promise((resolve, reject) => {
        const addresses = stops.map(stop => typeof stop === 'object' ? formatAddress(stop) : stop).filter((s: string) => s);

        if (addresses.length < 2) {
            return reject(new Warning(`Distance calculation requires at least 2 stops, got ${addresses.length}`));
        }

        loadGoogleMapsApi(apiKey).then(gmaps => {
            const {TravelMode, DirectionsService, DirectionsStatus} = gmaps;

            const directionsService = new DirectionsService;
            const origin = addresses.shift()!;
            const destination = addresses.pop()!;
            const waypoints = addresses.map((stop: any) => ({
                location: stop
            }));

            directionsService.route({
                origin,
                waypoints,
                destination,
                travelMode: TravelMode.DRIVING,
            }, (response: any, status: any) => {
                if (status === DirectionsStatus.OK) {
                    const distances = response.routes.flatMap((route: any) => route.legs.flatMap((leg: any) => leg.distance.value));
                    return resolve(arraySum(distances));
                } else {
                    return reject(new Error(status));
                }
            });
        });
    });
}

const distanceInMeters: Record<string,number> = {
    mi: 1609.34,
    km: 1000,
};

export function convertDistance(distance: number, unit: string, decimalDigits = 3) {
    unit = unit.toLowerCase()
    const meters = distanceInMeters[unit]
    if(!meters) throw new Error(`Distance unit not found in map: ${unit}`)
    return round(distance / meters, decimalDigits);
}
